import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import DayScoreCardModal from './DayScoreCardModal';

function formatMoney(value) {
  const number = Number(value) || 0;
  return `${number > 0 ? '+' : number < 0 ? '-' : ''}$${Math.abs(number).toFixed(2)}`;
}

function formatR(value) {
  const number = Number(value) || 0;
  return `${number > 0 ? '+' : ''}${number.toFixed(2)}R`;
}

function tone(value) {
  const number = Number(value) || 0;
  if (number > 0) return 'theme-success';
  if (number < 0) return 'theme-loss';
  return 'theme-text';
}

function Metric({ label, value, valueClass = 'theme-text' }) {
  return (
    <div className="theme-panel-alt theme-border min-w-0 rounded-xl border px-3 py-2.5">
      <div className="theme-text-muted text-[9px] font-bold uppercase tracking-[0.14em]">{label}</div>
      <div className={`${valueClass} mt-1 truncate text-lg font-bold`}>{value}</div>
    </div>
  );
}

function TradeSummary({ label, trade }) {
  if (!trade) return null;
  return (
    <div className="theme-panel-alt theme-border rounded-xl border p-3">
      <div className="theme-text-muted text-[9px] font-bold uppercase tracking-[0.14em]">{label}</div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="theme-text truncate text-sm font-bold">{trade.ticker}{trade.title ? ` · ${trade.title}` : ''}</div>
          <div className="theme-text-muted mt-0.5 text-xs">{trade.dateKey}{trade.rr != null ? ` · ${formatR(trade.rr)}` : ''}</div>
        </div>
        <div className={`${tone(trade.pnl)} flex-shrink-0 text-base font-bold`}>{formatMoney(trade.pnl)}</div>
      </div>
    </div>
  );
}

const WeeklyReviewStrip = ({
  review,
  dataChanged = false,
  showNotification = false,
  onNotificationSeen,
  onRefresh,
  onSaveReflections,
  saving = false,
  statusLabel,
  hideTrigger = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [weeklyCardOpen, setWeeklyCardOpen] = useState(false);
  const [reflections, setReflections] = useState(review?.reflections || {});
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;

  const updateOpen = useCallback((nextOpen) => {
    if (controlledOpen === undefined) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [controlledOpen, onOpenChange]);

  useEffect(() => {
    setReflections(review?.reflections || {});
  }, [review?.weekKey, review?.sourceHash, review?.reflections]);

  useEffect(() => {
    setNotificationDismissed(false);
  }, [review?.weekKey, review?.sourceHash]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEscape = (event) => {
      if (event.key === 'Escape' && !weeklyCardOpen) updateOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, updateOpen, weeklyCardOpen]);

  useEffect(() => {
    if (!isOpen) setWeeklyCardOpen(false);
  }, [isOpen]);

  const openReview = () => {
    updateOpen(true);
    setNotificationDismissed(true);
    onNotificationSeen?.();
  };

  const dismissNotification = () => {
    setNotificationDismissed(true);
    onNotificationSeen?.();
  };

  const updateReflection = (field, value) => {
    const next = { ...reflections, [field]: value };
    setReflections(next);
    onSaveReflections?.(next);
  };

  const patterns = useMemo(() => review?.tradeEnvironmentPatterns || [], [review]);
  const weeklyCardStats = useMemo(() => [
    { label: 'Total R', value: formatR(review?.totalR) },
    { label: 'Trades', value: String(review?.tradeCount || 0) },
    { label: 'Win rate', value: `${(Number(review?.winRate) || 0).toFixed(1)}%` },
    {
      label: 'Best day',
      value: review?.bestDay
        ? `${review.bestDay.label} ${formatMoney(review.bestDay.pnl)}`
        : '-',
    },
  ], [review]);
  const weeklyCardInitialNotes = useMemo(() => {
    const parts = [
      reflections.worked ? `Worked: ${reflections.worked}` : '',
      reflections.needsWork ? `Improve: ${reflections.needsWork}` : '',
      reflections.nextFocus ? `Next: ${reflections.nextFocus}` : '',
    ].filter(Boolean);
    return parts.join('\n').slice(0, 260);
  }, [reflections]);
  if (!review) return null;

  return (
    <>
      {showNotification && !isOpen && !notificationDismissed && (
        <div className="fixed right-4 top-4 z-[115] w-[min(340px,calc(100vw-2rem))]">
          <div className="theme-panel theme-border rounded-2xl border p-3.5 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="theme-accent-soft flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl">
                <CheckCircleIcon className="theme-accent h-4 w-4" />
              </span>
              <button type="button" onClick={openReview} className="min-w-0 flex-1 text-left">
                <div className="theme-text text-sm font-bold">Your weekly review is ready</div>
                <div className="theme-text-muted mt-0.5 text-xs">{review.accountName} · {review.weekLabel}</div>
              </button>
              <button type="button" onClick={dismissNotification} className="theme-text-muted theme-hover-surface rounded-lg p-1" aria-label="Dismiss weekly review notification">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {!hideTrigger && <section className="theme-panel theme-border rounded-2xl border px-3 py-3 shadow-subtle sm:px-4">
        <button type="button" onClick={openReview} className="flex w-full flex-col gap-3 text-left sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="theme-accent-soft flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl">
              <CalendarDaysIcon className="theme-accent h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="theme-text text-sm font-bold">Last week · {review.weekLabel}</div>
              <div className="theme-text-muted mt-0.5 text-xs">{review.accountName}{review.limitedSample ? ' · Small sample' : ''}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:justify-end">
            <span className={`${tone(review.totalPnl)} text-sm font-bold`}>{formatMoney(review.totalPnl)}</span>
            <span className="theme-text text-sm font-semibold">{review.tradeCount} trades</span>
            <span className={`${tone(review.totalR)} text-sm font-semibold`}>{formatR(review.totalR)}</span>
            <span className="theme-accent inline-flex items-center gap-1 text-xs font-bold">Open review <ArrowRightIcon className="h-3.5 w-3.5" /></span>
          </div>
        </button>
      </section>}

      {isOpen && (
        <div className="fixed inset-0 z-[120]">
          <button type="button" className="absolute inset-0 h-full w-full cursor-default bg-black/50 backdrop-blur-[2px]" onClick={() => updateOpen(false)} aria-label="Close weekly review" />
          <section role="dialog" aria-modal="true" aria-labelledby="weekly-review-title" className="theme-app-shell theme-border absolute bottom-0 right-0 top-0 flex w-full flex-col border-l shadow-2xl sm:w-[66vw] sm:max-w-[900px]">
            <header className="theme-panel theme-border flex items-center justify-between gap-4 border-b px-5 py-4 sm:px-6">
              <div className="min-w-0">
                <div className="theme-accent text-[10px] font-bold uppercase tracking-[0.2em]">Weekly review</div>
                <h2 id="weekly-review-title" className="theme-text mt-1 truncate text-xl font-bold">{review.accountName} · {review.weekLabel}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setWeeklyCardOpen(true)}
                  className="theme-accent-soft theme-accent inline-flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-bold transition-opacity hover:opacity-85"
                >
                  <PhotoIcon className="h-4 w-4" /> Weekly card
                </button>
                {dataChanged && (
                  <button type="button" onClick={onRefresh} className="theme-btn-secondary inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold">
                    <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh data
                  </button>
                )}
                <button type="button" onClick={() => updateOpen(false)} className="theme-hover-surface theme-text-muted rounded-lg p-2" aria-label="Close weekly review drawer">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </header>

            <div className="themed-scrollbar flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Metric label="Net P&L" value={formatMoney(review.totalPnl)} valueClass={tone(review.totalPnl)} />
                <Metric label="Total R" value={formatR(review.totalR)} valueClass={tone(review.totalR)} />
                <Metric label="Trades" value={review.tradeCount} />
                <Metric label="Win rate" value={`${review.winRate.toFixed(1)}%`} />
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {review.bestDay && <Metric label="Best day" value={`${review.bestDay.label} · ${formatMoney(review.bestDay.pnl)}`} valueClass={tone(review.bestDay.pnl)} />}
                {review.worstDay && <Metric label="Worst day" value={`${review.worstDay.label} · ${formatMoney(review.worstDay.pnl)}`} valueClass={tone(review.worstDay.pnl)} />}
                {review.strongestSession && <Metric label="Strongest session" value={`${review.strongestSession.label} · ${formatMoney(review.strongestSession.totalPnl)}`} valueClass={tone(review.strongestSession.totalPnl)} />}
                {review.lossClassification && <Metric label="Common avoidable loss" value={review.lossClassification.classification} valueClass="theme-text" />}
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <TradeSummary label="Best trade" trade={review.bestTrade} />
                <TradeSummary label="Worst trade" trade={review.worstTrade} />
              </div>

              {patterns.length > 0 && (
                <section className="mt-5">
                  <h3 className="theme-text text-sm font-bold">Trade environment patterns</h3>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    {patterns.map((pattern) => (
                      <div key={`${pattern.fieldId}-${pattern.value}`} className="theme-panel-alt theme-border rounded-xl border p-3">
                        <div className="theme-text-muted text-[9px] font-bold uppercase tracking-[0.12em]">{pattern.label}</div>
                        <div className="theme-text mt-1 text-sm font-bold">{pattern.value}</div>
                        <div className="theme-text-muted mt-1 text-xs">{pattern.trades} trades · <span className={tone(pattern.pnl)}>{formatMoney(pattern.pnl)}</span></div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className="mt-5 grid gap-3 sm:grid-cols-3">
                {[
                  ['worked', 'What worked'],
                  ['needsWork', 'What needs work'],
                  ['nextFocus', "Next week's focus"],
                ].map(([field, label]) => (
                  <label key={field} className="block">
                    <span className="theme-text-muted text-[10px] font-bold uppercase tracking-[0.13em]">{label}</span>
                    <textarea
                      value={reflections[field] || ''}
                      onChange={(event) => updateReflection(field, event.target.value)}
                      placeholder="Optional"
                      rows={5}
                      className="theme-input mt-2 w-full resize-none rounded-xl border px-3 py-2.5 text-sm"
                    />
                  </label>
                ))}
              </section>
              <div className="theme-text-muted mt-3 text-right text-[10px]">{saving ? 'Saving...' : (statusLabel || 'Saved automatically')}</div>
            </div>
          </section>
        </div>
      )}

      <DayScoreCardModal
        isOpen={weeklyCardOpen}
        onClose={() => setWeeklyCardOpen(false)}
        cardType="week"
        heading="Weekly Score Card"
        metricLabel="Net weekly P&L"
        dateKey={review.weekKey}
        dateLabel={review.weekLabel}
        pnl={review.totalPnl}
        statCards={weeklyCardStats}
        notesLabel="Weekly takeaway"
        notesPlaceholder="Add a clean takeaway for this week..."
        initialNotes={weeklyCardInitialNotes}
        fileNamePrefix="weekly-card"
      />
    </>
  );
};

export default WeeklyReviewStrip;
